import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'sonner';

import {
  useGetAdminLoanChecklistMatricesQuery,
  usePutAdminLoanChecklistMatrixMutation,
  useResetAdminLoanChecklistMatricesMutation,
  type LoanChecklistMatrixItem,
} from '@/apis/extended/admin/loanChecklistMatrixApi';

const RULE_LABELS: Record<string, string> = {
  'refinance:agency': 'Refinance — agency / URLA-style',
  'refinance:standard': 'Refinance — standard (non-agency products)',
  dscr: 'DSCR / investor purchase',
  'purchase:agency': 'Purchase — agency (Conventional, FHA, VA, USDA)',
};

function normalizeItems(
  items: LoanChecklistMatrixItem[]
): LoanChecklistMatrixItem[] {
  return items.map((row) => ({
    key: String(row.key ?? '').trim(),
    label: String(row.label ?? '').trim(),
    required: row.required !== false,
    requiredForSubmit: row.requiredForSubmit !== false,
  }));
}

function validateItems(items: LoanChecklistMatrixItem[]): string | null {
  const keys = normalizeItems(items).map((i) => i.key);
  if (keys.some((k) => !k)) {
    return 'Every row needs a document key (e.g. id_government).';
  }
  if (keys.some((k) => k.length > 120)) {
    return 'Keys must be 120 characters or fewer.';
  }
  const set = new Set(keys);
  if (set.size !== keys.length) {
    return 'Duplicate keys are not allowed.';
  }
  return null;
}

export const AdminLoanChecklistMatricesPage = () => {
  const { data, isLoading, refetch } = useGetAdminLoanChecklistMatricesQuery();
  const [putMatrix, { isLoading: saving }] =
    usePutAdminLoanChecklistMatrixMutation();
  const [resetMatrices, { isLoading: resetting }] =
    useResetAdminLoanChecklistMatricesMutation();

  const [drafts, setDrafts] = useState<
    Record<string, LoanChecklistMatrixItem[]>
  >({});
  const [resetOpen, setResetOpen] = useState(false);

  const matrices = data?.matrices ?? [];
  const knownKeys = data?.knownRuleKeys ?? [];

  useEffect(() => {
    if (!matrices.length) return;
    setDrafts((prev) => {
      const next = { ...prev };
      for (const m of matrices) {
        if (next[m.ruleKey] == null) {
          next[m.ruleKey] = normalizeItems(m.items ?? []);
        }
      }
      return next;
    });
  }, [matrices]);

  const orderedKeys = useMemo(() => {
    const fromApi = matrices.map((m) => m.ruleKey);
    const merged = [...new Set([...knownKeys, ...fromApi])];
    return merged.sort();
  }, [matrices, knownKeys]);

  const updateDraft = useCallback(
    (ruleKey: string, rows: LoanChecklistMatrixItem[]) => {
      setDrafts((d) => ({ ...d, [ruleKey]: rows }));
    },
    []
  );

  const handleSave = async (ruleKey: string) => {
    const items = drafts[ruleKey];
    if (!items?.length) {
      toast.error('Add at least one checklist row.');
      return;
    }
    const err = validateItems(items);
    if (err) {
      toast.error(err);
      return;
    }
    try {
      await putMatrix({
        ruleKey,
        items: normalizeItems(items),
      }).unwrap();
      toast.success(`Saved ${ruleKey}`);
      refetch();
    } catch {
      toast.error('Save failed');
    }
  };

  const handleResetConfirm = async () => {
    try {
      await resetMatrices().unwrap();
      setDrafts({});
      toast.success('Checklist matrices restored to code defaults');
      setResetOpen(false);
      refetch();
    } catch {
      toast.error('Reset failed');
    }
  };

  const emptyRow = (): LoanChecklistMatrixItem => ({
    key: '',
    label: '',
    required: true,
    requiredForSubmit: true,
  });

  if (isLoading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Loading checklist matrices…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Checklists
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        These templates apply when a new loan application is created (or when
        the product type changes in draft and the checklist is rebuilt). Items
        marked required but not &ldquo;required for submit&rdquo; are tracked
        but do not block borrower submission.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Button
          variant="outlined"
          color="warning"
          onClick={() => setResetOpen(true)}
          disabled={resetting}
        >
          Reset all to code defaults
        </Button>
      </Box>

      <Dialog open={resetOpen} onClose={() => setResetOpen(false)}>
        <DialogTitle>Reset checklist matrices?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This deletes admin-edited rows in the database and re-seeds the four
            program templates from server defaults. Existing loan applications
            are not changed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={handleResetConfirm}
            disabled={resetting}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>

      {orderedKeys.map((ruleKey) => {
        const matrix = matrices.find((m) => m.ruleKey === ruleKey);
        const rows = drafts[ruleKey] ?? normalizeItems(matrix?.items ?? []);

        return (
          <Accordion key={ruleKey} defaultExpanded sx={{ mb: 1 }}>
            <AccordionSummary
              expandIcon={<i className="fa-light fa-chevron-down" />}
            >
              <Box>
                <Typography fontWeight={600}>
                  {RULE_LABELS[ruleKey] ?? ruleKey}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  ruleKey: {ruleKey}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Paper variant="outlined" sx={{ overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Key</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell align="center">Required</TableCell>
                      <TableCell align="center">Blocks submit</TableCell>
                      <TableCell width={56} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.key}
                            onChange={(e) => {
                              const next = [...rows];
                              next[idx] = { ...row, key: e.target.value };
                              updateDraft(ruleKey, next);
                            }}
                            placeholder="e.g. id_government"
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.label}
                            onChange={(e) => {
                              const next = [...rows];
                              next[idx] = { ...row, label: e.target.value };
                              updateDraft(ruleKey, next);
                            }}
                            placeholder="Borrower-facing label"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={row.required}
                            onChange={(_, v) => {
                              const next = [...rows];
                              next[idx] = { ...row, required: v };
                              updateDraft(ruleKey, next);
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Switch
                            size="small"
                            checked={row.requiredForSubmit}
                            onChange={(_, v) => {
                              const next = [...rows];
                              next[idx] = { ...row, requiredForSubmit: v };
                              updateDraft(ruleKey, next);
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            aria-label="Remove row"
                            onClick={() => {
                              const next = rows.filter((_, i) => i !== idx);
                              updateDraft(ruleKey, next);
                            }}
                          >
                            <i className="fa-light fa-trash-can" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => updateDraft(ruleKey, [...rows, emptyRow()])}
                >
                  Add row
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleSave(ruleKey)}
                  disabled={saving || rows.length === 0}
                >
                  Save {ruleKey}
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
};
